import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Skeleton,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  AthenaTableGQLType,
  AthenaTablePropertyGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useGetAthenaTablesForOrganizationQuery } from 'packages/athena/requests/requests.gql.generated';
import {
  athenaDataTypeToIcon,
  filterAthenaTablesForCreditFacilities,
} from 'packages/athena/ui/utils';
import { colors } from 'packages/common-frontend/colors';
import {
  doesSearchQueryExistInString,
  SearchBar,
  SearchRow,
} from 'packages/common-frontend/components/SearchAndCopyComponents';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCommonWipData } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCommonWipData';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const AthenaSchemaSidebarLoadingSkeleton = () => {
  return (
    <Box
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      overflow="auto"
      maxHeight="400px"
      height="400px"
      backgroundColor="white"
    >
      <Box position="sticky" top="0">
        <SearchBar query="" onChangeQuery={() => {}} isDisabled />
      </Box>
      <VStack spacing="8px" alignItems="left" p="0 16px">
        {_.times(8, () => (
          <Skeleton h="30px" />
        ))}
      </VStack>
    </Box>
  );
};

const AthenaTableSchemaAccordionPanel = ({
  athenaTable,
}: {
  athenaTable: AthenaTableGQLType;
}) => {
  return (
    <AccordionPanel py="0">
      <VStack spacing="8px" alignItems="left">
        {_.map(athenaTable.properties, (property) => (
          <SearchRow
            rowValue={property.name}
            key={`accordion-panel-row-${property.name}`}
            leftIcon={
              athenaDataTypeToIcon[property.type]
                ? {
                    iconName: athenaDataTypeToIcon[property.type],
                    tooltip: property.type,
                  }
                : undefined
            }
          />
        ))}
      </VStack>
    </AccordionPanel>
  );
};

const AthenaSchemaSidebarBody = ({
  athenaTables,
}: {
  athenaTables: AthenaTableGQLType[];
}) => {
  const [query, setQuery] = useState<string>('');
  const [openAccordionIndices, setOpenAccordionIndices] = useState<
    number | number[]
  >([0]);

  const filteredAthenaTableSchema = useMemo(() => {
    const filteredAthenaTables: AthenaTableGQLType[] = [];
    _.forEach(athenaTables, (athenaTable) => {
      // if the tableName itself is in the search query, add table schema with all its properties
      if (
        doesSearchQueryExistInString({
          queryString: query,
          stringToEvaluate: athenaTable.tableName,
        })
      ) {
        filteredAthenaTables.push(athenaTable);
      } else {
        const filteredPropertiesForTable: AthenaTablePropertyGQLType[] = [];

        // if the tableName itself doesn't match the search query
        // check each property individually
        _.forEach(athenaTable.properties, (property) => {
          if (
            doesSearchQueryExistInString({
              queryString: query,
              stringToEvaluate: property.name,
            })
          ) {
            filteredPropertiesForTable.push(property);
          }
        });

        // if any properties matched the query term, add the table with the filtered properties
        if (filteredPropertiesForTable.length) {
          filteredAthenaTables.push({
            tableName: athenaTable.tableName,
            properties: filteredPropertiesForTable,
          });
        }
      }
    });

    return filteredAthenaTables;
  }, [athenaTables, query]);

  const onChangeQuery = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);

      if (!newQuery) {
        setOpenAccordionIndices([0]);
      } else {
        const numberOfFilteredGroups = athenaTables.length;
        setOpenAccordionIndices([...Array(numberOfFilteredGroups).keys()]);
      }
    },
    [athenaTables],
  );

  const doAthenaTablesExist = !!athenaTables.length;

  return (
    <Box>
      <Box position="sticky" top="0">
        <SearchBar
          query={query}
          onChangeQuery={onChangeQuery}
          isDisabled={!doAthenaTablesExist}
        />
      </Box>
      {doAthenaTablesExist ? (
        <Accordion
          allowMultiple
          index={openAccordionIndices}
          onChange={(openIndices) => setOpenAccordionIndices(openIndices)}
          maxW="400px"
          overflow="auto"
        >
          {_.map(filteredAthenaTableSchema, (athenaTable) => {
            return (
              <AccordionItem
                border="0"
                key={`accordion-item-${athenaTable.tableName}`}
              >
                <AccordionButton width="100%">
                  <Box flex="1" overflow="hidden" textAlign="left">
                    <Text isTruncated>{athenaTable.tableName}</Text>
                  </Box>
                  <AccordionIcon ml="8px" flexShrink={0} />
                </AccordionButton>
                <AthenaTableSchemaAccordionPanel athenaTable={athenaTable} />
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        <Text>No available data tables to use for inputs.</Text>
      )}
    </Box>
  );
};

export const AthenaSchemaSidebar = ({
  creditFacilityId,
  isLenderWipMode,
  organizationId: organizationIdProp,
}: {
  creditFacilityId: string;
  isLenderWipMode: boolean;
  organizationId?: string;
}) => {
  const toast = useToast();
  const user = useUserAuthOrThrow();

  const organizationId = organizationIdProp ?? user.user.organizationId;

  const { creditFacilitySlug } = useGetCommonWipData({
    creditFacilityId,
  });

  const {
    data: athenaTablesData,
    loading: isAthenaTablesDataLoading,
  } = useGetAthenaTablesForOrganizationQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId },
  });

  // @dani add better skeleton for loading
  if (!athenaTablesData || isAthenaTablesDataLoading) {
    return <AthenaSchemaSidebarLoadingSkeleton />;
  }

  finleyAssert(
    creditFacilitySlug,
    `creditFacilitySlug must exist for [creditFacilityId: ${creditFacilityId}]`,
  );
  const athenaTablesForCreditFacility = filterAthenaTablesForCreditFacilities({
    athenaTables: athenaTablesData.athenaTables,
    creditFacilitySlugs: [creditFacilitySlug],
  });

  return (
    <Box
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      overflow="auto"
      height="400px"
      backgroundColor={isLenderWipMode ? 'white' : undefined}
    >
      <AthenaSchemaSidebarBody athenaTables={athenaTablesForCreditFacility} />
    </Box>
  );
};
